import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { commonComponents } from '@app/shared/common components/common components';
import * as d3 from 'd3';
import { HierarchicalData } from './relation-graph.interface';

declare let ResizeObserver: any;

@Component({
  standalone: false,
  selector: 'app-graph-module-relation-graph',
  templateUrl: './relation-graph.component.html',
  styleUrls: ['./relation-graph.component.css'],
})
export class RelationGraphComponent<T> implements OnInit, OnChanges, OnDestroy {
  @Input() hierarchicalData: HierarchicalData<T>;
  @Input() names: string;
  @Output() nodeClick = new EventEmitter<T>();
  @ViewChild('treeContainer', { read: ElementRef }) treeContainer!: ElementRef;

  private svg: any;
  private width = 1200;
  private height = 950;
  private resizeObserver: any;
  private simulation: d3.Simulation<d3.SimulationNodeDatum, any>;

  constructor() {}

  ngOnInit(): void {
    this.setupResizeObserver();
  }

  ngOnChanges(): void {
    this.createSvg();
    if (this.hierarchicalData && this.names) {
      this.updateChart(this.hierarchicalData);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.simulation) {
      this.simulation.stop();
    }
  }

  private createSvg(): void {
    if (this.svg) return;

    this.width = this.treeContainer.nativeElement.clientWidth;
    this.height = this.treeContainer.nativeElement.clientHeight;

    this.svg = d3
      .select(this.treeContainer.nativeElement)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('overflow', 'hidden')
      .attr('margin', '40px');

    this.svg
      .append('defs')
      .append('marker')
      .attr('id', 'arrow')
      .attr('class', 'graph-content')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 15)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#999');
  }

  private updateChart(data: HierarchicalData<T>): void {
    this.svg.select('.graph-content').selectAll('*').remove();

    this.simulation = d3
      .forceSimulation()
      .force(
        'link',
        d3
          .forceLink()
          .id((d: any) => d.id)
          .distance(10),
      )
      .force('charge', d3.forceManyBody().strength(-50))
      .force('center', d3.forceCenter(this.width / 2, this.height / 2))
      .force('collide', d3.forceCollide().radius(60).iterations(2));

    const root = d3.hierarchy(data);
    const links = root.links();
    const nodes = root.descendants();

    const link = this.svg
      .append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#aaa')
      .attr('stroke-width', 3)
      .attr('stroke-height', 10)
      .attr('marker-end', 'url(#arrow)');

    const node = this.svg
      .append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('cursor', 'pointer')
      .call(
        d3
          .drag()
          .on('start', (event, d) => this.dragStarted(event, d, this.simulation))
          .on('drag', (event, d) => this.dragged(event, d))
          .on('end', (event, d) => this.dragEnded(event, d, this.simulation)),
      )
      .on('click', (_event: any, d: any) => {
        if (d) {
          const clickedNodeData = d.data;
          if (clickedNodeData) {
            this.nodeClick.emit(clickedNodeData);
            this.nodecircle(node, clickedNodeData);
            this.noaddata(node, radius);
          } else {
            console.error('No matching data found for:', d);
          }
        } else {
          this.nodeClick.emit(undefined);
          this.nodecircle(node, undefined);
          this.noaddata(node, radius);
        }
      });

    this.nodecircle(node, this.names);
    node
      .filter((d: any) => d.children)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 30)
      .attr('height', 30);
    const radius = 50;

    this.noaddata(node, radius);

    this.simulation.nodes(nodes as any).on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);
      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    (this.simulation.force('link') as any).links(links);
  }
  nodecircle(node: any, clickedNodeData: any) {
    const { OK, MATCH_FOUND, Error } = commonComponents.CandidateList;
    node
      .append('circle')
      .attr('r', 50)
      .attr('fill', (d: any) => {
        if (d.data.name === this.names) {
          return '#0c41ac';
        } else if (d.data.name === 'Manual State' || d.data.name === 'Manual County') {
          return '#2874a6 ';
        } else if (existsIn(OK, d.data.assessment_decision)) {
          return 'green';
        } else if (existsIn(MATCH_FOUND, d.data.assessment_decision)) {
          return '#dd0202';
          // }else if (d.data.status === MATCH_FOUND[d.data.status]) {
          //   return '#dd0202';
          // }else if (d.data.status === OK[d.data.status]) {
          //   return 'green';
        } else if (existsIn(Error, d.data.assessment_decision)) {
          return '#c59c02';
        } else {
          return 'gray';
        }
      })
      .attr('stroke', (d: any) => {
        if (clickedNodeData === this.names) {
          return this.names === d.data.name ? 'black' : '#c4cbc7';
        } else if (clickedNodeData.name === this.names) {
          return this.names === d.data.name ? 'black' : '#c4cbc7';
        } else if (clickedNodeData.name === 'Manual County' || clickedNodeData.name === 'Manual State') {
          return d.data.name === clickedNodeData.name ? 'black' : '#c4cbc7';
        } else if (clickedNodeData.api_name && clickedNodeData.api_name === d.data.api_name) {
          return 'black';
        } else {
          return '#c4cbc7';
        }
      })
      .attr('stroke-width', 3);
  }
  noaddata(node: any, radius: any) {
    node
      .append('text')
      .attr('dy', -20)
      .attr('x', 0)
      .attr('y', -radius + 20)
      .style('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('fill', 'white')
      .style('overflow', 'hidden')
      .attr('width', 40)
      .attr('height', 30)
      .style('display', 'flex')
      .style('justify-content', 'center')
      .style('align-items', 'center')
      .each(function (d: any) {
        const self = d3.select(this);
        const words = d.children ? (d.data.name ? d.data.name.split(' ') : []) : d.data.api_name.split(' ');
        const lineHeight = 10;
        let tspan = self.text(null).append('tspan').attr('x', 0).attr('dy', 0);
        let line: string[] = [];
        let lineNumber = 0.8;

        words.forEach((word: null) => {
          line.push(word);
          tspan.text(line.join(' '));
          if (self.node().getComputedTextLength() > 10) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            tspan = self
              .append('tspan')
              .attr('x', 0)
              .attr('dy', ++lineNumber + lineHeight)
              .text(word);
          }
        });
      });
  }

  private dragStarted(event: any, d: any, simulation: any): void {
    if (!event.active) simulation.alphaTarget(0.3).restart();
    if (typeof d === 'object') {
      d.fx = d.x;
      d.fy = d.y;
    }
  }
  private dragged(event: any, d: any): void {
    if (typeof d === 'object') {
      d.fx = event.x;
      d.fy = event.y;
    }
  }

  private dragEnded(event: any, d: any, simulation: any): void {
    if (!event.active) simulation.alphaTarget(0);
    if (typeof d === 'object') {
      d.fx = null;
      d.fy = null;
    }
  }

  private setupResizeObserver(): void {
    if (!this.treeContainer) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.handleResize(width, height);
      }
    });

    this.resizeObserver.observe(this.treeContainer.nativeElement);
  }

  private handleResize(width: number, height: number): void {
    this.width = width;
    this.height = height;

    if (!this.svg) return;
    this.svg.attr('viewBox', `0 0 ${this.width} ${this.height}`);

    if (this.simulation) {
      this.simulation.force('center', d3.forceCenter(this.width / 2, this.height / 2));
      // Use a small delay to ensure DOM updates are complete before restarting simulation
      setTimeout(() => {
        if (this.simulation) {
          this.simulation.alpha(0.3).restart();
        }
      }, 50);
    }
  }
}

function existsIn(obj: object, proposedKey: string): boolean {
  if (!obj || typeof obj !== 'object' || !proposedKey) {
    return false;
  }

  for (const key in obj) {
    if (obj.hasOwnProperty(key) && key.toLowerCase() === proposedKey.toLowerCase()) {
      return true;
    }
  }
  return false;
}
